import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

@Injectable()
export abstract class CommonService<T> {
  protected http = inject(HttpClient);
  protected apiUrl: string;

  constructor(protected endpoint: string) {
    this.apiUrl = `${environment.URL_API}/${endpoint}`;
  }

  /**
   * Obtiene todos los registros
   */
  getAll(params?: HttpParams): Observable<T[]> {
    return this.http.get<T[]>(this.apiUrl, { params });
  }

  /**
   * Obtiene un registro por ID
   */
  getById(id: number | string): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${id}`);
  }

  /**
   * Crea un nuevo registro
   */
  create(entity: T): Observable<T> {
    return this.http.post<T>(this.apiUrl, entity);
  }

  /**
   * Actualiza un registro existente
   */
  update(id: number | string, entity: Partial<T>): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json'
    });
    return this.http.patch<T>(`${this.apiUrl}/${id}`, entity, { headers });
  }

  /**
   * Actualiza parcialmente un registro
   */
  patch(id: number | string, entity: Partial<T>): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json'
    });
    return this.http.patch<T>(`${this.apiUrl}/${id}`, entity, { headers });
  }

  /**
   * Elimina un registro
   */
  delete(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Obtiene registros paginados
   */
  getPaginated(page: number = 1, pageSize: number = environment.REGISTROS_PER_PAGE, params?: HttpParams): Observable<PaginatedResponse<T>> {
    const paginationParams = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    
    const finalParams = params ? paginationParams.appendAll(params as any) : paginationParams;
    
    return this.http.get<PaginatedResponse<T>>(`${this.apiUrl}/paginated`, { params: finalParams });
  }

  /**
   * Busca registros
   */
  search(searchTerm: string, params?: HttpParams): Observable<T[]> {
    const searchParams = new HttpParams().set('q', searchTerm);
    const finalParams = params ? searchParams.appendAll(params as any) : searchParams;
    
    return this.http.get<T[]>(`${this.apiUrl}/search`, { params: finalParams });
  }
}

